import { useMemo, useState } from "react";
import { Alert, Button, Group, Stack, Switch, TagsInput, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  ZRoomSyncSettings,
  ZRoomSyncToggleKey,
  MAX_ML_CEILING_ROOMS_PER_RUN,
  type IRoomSyncSettings,
  type IRoomSyncToggleKey,
} from "@multiconsult-group/multiplacer-core";
import { createSelector } from "@reduxjs/toolkit";
import { openapi, usePutMultiplacerProjectsRoomSyncSettingsMutation } from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";

const DEFAULT_SETTINGS = ZRoomSyncSettings.parse({});
const options = [
  { key: ZRoomSyncToggleKey.enum.drofusRooms, label: "Hent romdata fra dRofus" },
  {
    key: ZRoomSyncToggleKey.enum.drofusSpecifiedArticles,
    label: "Hent artikkelspesifikasjoner fra dRofus",
  },
  {
    key: ZRoomSyncToggleKey.enum.deriveRoomExtraIfcInfo,
    label: "Beregn ekstra IFC-informasjon (himlings-MMI)",
  },
  {
    key: ZRoomSyncToggleKey.enum.validateSpecifiedArticles,
    label: "Valider spesifiserte artikler mot modellen",
  },
  {
    key: ZRoomSyncToggleKey.enum.mlPredictCeilingDevices,
    label: "AI-prediksjon av himlingskomponenter",
  },
] satisfies ReadonlyArray<{ key: IRoomSyncToggleKey; label: string }>;

export function RoomSyncSettingsForm({
  projectNo,
  canEdit,
}: {
  projectNo: number;
  canEdit: boolean;
}) {
  const selectSettings = useMemo(
    () =>
      createSelector(
        [openapi.endpoints.getMultiplacerProjectsSingleProject.select({ projectNo })],
        (query) => query.data?.roomSync
      ),
    [projectNo]
  );
  const settings = useAppSelector(selectSettings);
  return (
    <RoomSyncSettingsEditor
      key={`${projectNo}:${JSON.stringify(settings)}`}
      projectNo={projectNo}
      canEdit={canEdit}
      initial={settings}
    />
  );
}

function RoomSyncSettingsEditor({
  projectNo,
  canEdit,
  initial,
}: {
  projectNo: number;
  canEdit: boolean;
  initial?: IRoomSyncSettings;
}) {
  const dispatch = useAppDispatch();
  const [draft, setDraft] = useState<IRoomSyncSettings>(initial ?? DEFAULT_SETTINGS);
  const [error, setError] = useState<string | null>(null);
  const [save, { isLoading }] = usePutMultiplacerProjectsRoomSyncSettingsMutation();
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial ?? DEFAULT_SETTINGS);

  async function handleSave() {
    setError(null);
    try {
      const saved = await save({ body: { projectNo, roomSync: draft } }).unwrap();
      dispatch(
        openapi.util.updateQueryData(
          "getMultiplacerProjectsSingleProject",
          { projectNo },
          (project) => {
            project.roomSync = saved;
          }
        )
      );
      dispatch(openapi.util.invalidateTags(["projects"]));
      notifications.show({ color: "green", message: "Innstillinger for romsynk er lagret." });
    } catch (cause) {
      const message = (cause as { data?: { message?: string } })?.data?.message;
      setError(message || "Kunne ikke lagre innstillingene. Prøv igjen.");
    }
  }

  return (
    <Stack gap="sm">
      <Text fw={600} size="sm">
        Jobber i romsynken
      </Text>
      <Text size="sm">MCBIM-rom og MCBIM-snapshots skal alltid kjøres.</Text>
      <Text size="sm" c="dimmed">
        Lagrede valg gjelder fra neste synk. AI-batchjobben behandler maksimalt{" "}
        {MAX_ML_CEILING_ROOMS_PER_RUN} rom per kjøring, uten PNS-filter. Rom med eksisterende
        AI-prediksjoner eller manglende romgeometri/himlingsgrid hoppes over.
      </Text>
      {!initial && (
        <Text size="sm" c="dimmed">
          Ingen innstillinger er lagret. Standardvalgene nedenfor brukes ved synk.
        </Text>
      )}
      {options.map(({ key, label }) => (
        <Switch
          key={key}
          label={label}
          checked={draft[key]}
          disabled={!canEdit || isLoading}
          onChange={(event) => setDraft({ ...draft, [key]: event.currentTarget.checked })}
        />
      ))}
      <TagsInput
        label="Disipliner for standardromsvalidering"
        description="For eksempel E og V. Ingen disipliner betyr at valideringen er av."
        data={["E", "V"]}
        value={draft.standardRoomValidationDisciplines}
        disabled={!canEdit || isLoading}
        onChange={(values) => setDraft({ ...draft, standardRoomValidationDisciplines: values })}
      />
      {error && <Alert color="red">{error}</Alert>}
      {canEdit && (
        <Group>
          <Button
            onClick={() => void handleSave()}
            loading={isLoading}
            disabled={!dirty && !!initial}
          >
            Lagre innstillinger
          </Button>
          <Button
            variant="light"
            disabled={!dirty || isLoading}
            onClick={() => {
              setDraft(initial ?? DEFAULT_SETTINGS);
              setError(null);
            }}
          >
            Tilbakestill
          </Button>
        </Group>
      )}
    </Stack>
  );
}
